// routes
import Router from './routes';
// theme
import ThemeConfig from './theme';
// hooks
import useAuth from './hooks/useAuth';
import { useEffect } from 'react';
// components
import Settings from './components/settings';
import RtlLayout from './components/RtlLayout';
import ScrollToTop from './components/ScrollToTop';
import LoadingScreen from './components/LoadingScreen';
import GoogleAnalytics from './components/GoogleAnalytics';
import NotistackProvider from './components/NotistackProvider';
import ThemePrimaryColor from './components/ThemePrimaryColor';
import NewRelicRouteTracker from './components/NewRelicRouteTracker';
import FloatingChatButton from './pages/dashboard/ChatModuleSignwise/FloatingChatButton';
// contexts
import RouteChangeCancellation from './components/RouteChangeCancellation';
import { initializeGlobalApiCancellation } from './utils/globalApiCancellationManager';
import { loadNewRelic } from './utils/newRelicLoader';
import { registerGlobalAxiosNewRelicInterceptors } from './utils/axios';
import axios from 'axios';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
// import {
//   APP_ZOOM_MODE_COMPACT,
//   APP_ZOOM_MODE_VISUAL,
//   clampZoomPercent,
//   getLoggedInUserId,
//   getStoredZoomForUser,
//   LOGIN_APP_ZOOM_PERCENT
// } from './utils/appZoomPreference';
import { scheduleAutocompleteZoomFix, subscribeGlobalAutocompleteZoomFix } from './utils/autocompleteZoom';
import { scheduleFullCalendarZoomFix, subscribeGlobalFullCalendarZoomFix } from './utils/fullCalendarZoom';
import { scheduleTabsIndicatorZoomFix, subscribeTabsIndicatorZoomFix } from './utils/tabsIndicatorZoom';

// ----------------------------------------------------------------------

// CHANGED: software zoom disabled — users rely on browser zoom (Ctrl/Cmd +/-).
// Preserved for reference; was used to apply CSS zoom in Chromium.
/*
const isChromiumBrowser = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const brands = navigator.userAgentData?.brands || [];
  if (Array.isArray(brands) && brands.length > 0) {
    return brands.some((brandEntry) => /Chrom|Chrome|Edge|Opera/i.test(brandEntry?.brand || ''));
  }
  const userAgent = navigator.userAgent || '';
  const hasChromiumToken = /(Chrome|Chromium|Edg|OPR)/i.test(userAgent);
  const hasFirefoxOrSafariToken = /(Firefox|Safari)/i.test(userAgent) && !/(Chrome|Chromium|Edg|OPR)/i.test(userAgent);
  return hasChromiumToken && !hasFirefoxOrSafariToken;
};
*/

export default function App() {
  const { isInitialized,isAuthenticated  } = useAuth();
  console.log('isInitialized', isInitialized)
  console.log('isAuthenticated', isAuthenticated)
  let userData = null
  try {
    const raw = localStorage.getItem('user')
    if (raw && raw !== 'null' && raw !== 'undefined') {
      userData = JSON.parse(raw)
    }
  } catch (e) {
    userData = null
  }
  console.log('userData+++++++++++++++', userData)

  useEffect(() => {
    loadNewRelic();
    registerGlobalAxiosNewRelicInterceptors();
  }, []);

  // Fetch maintenance banner details globally on app load and auth state changes
  useEffect(() => {
    const fetchMaintenanceBanner = async () => {
      try {
        const response = await axios.post(`${REST_API_END_POINT}settings/get-maintenance-banner`);
        if (response.data.status === 1) {
          const isShow = response.data.show === 1 || response.data.show === true || response.data.show === '1';
          const text = response.data.text || '';
          localStorage.setItem('maintenance_banner_show', String(isShow));
          localStorage.setItem('maintenance_banner_text', text);
          window.dispatchEvent(new Event('maintenance_banner_update'));
        }
      } catch (err) {
        console.log('Error fetching maintenance banner globally', err);
      }
    };
    fetchMaintenanceBanner();
  }, [isAuthenticated]);

  // Initialize global API cancellation on app start
  useEffect(() => {
    initializeGlobalApiCancellation();
  }, []);

  // CHANGED: software zoom removed — always use native 100% layout; users adjust via browser zoom.
  // Original CSS zoom logic preserved below in comments.
  useEffect(() => {
    const rootElement = document.getElementById('root');
    const htmlElement = document.documentElement;
    if (!rootElement || !htmlElement) return undefined;

    const previousRootZoom = rootElement.style.zoom || '';
    const previousRootWidth = rootElement.style.width || '';
    const previousRootMaxWidth = rootElement.style.maxWidth || '';
    const previousBodyOverflowX = document.body.style.overflowX || '';
    const previousHtmlOverflowX = htmlElement.style.overflowX || '';
    const previousDensity = htmlElement.getAttribute('data-app-density') || '';
    const previousDensityScale = htmlElement.style.getPropertyValue('--sw-density-scale') || '';

    const applyDisplayPreferences = () => {
      // Active: native 100% — no CSS zoom on #root
      rootElement.style.zoom = '';
      rootElement.style.width = '';
      rootElement.style.maxWidth = '';
      document.body.style.overflowX = '';
      htmlElement.style.overflowX = '';
      htmlElement.removeAttribute('data-app-zoom-below-100');
      htmlElement.style.removeProperty('--sw-app-zoom-scale');
      htmlElement.setAttribute('data-app-density', 'normal');
      htmlElement.style.setProperty('--sw-density-scale', '1');

      /*
      // Original software zoom (read stored preference / event, apply CSS zoom in Chromium):
      const userId = getLoggedInUserId();
      const isLoggedIn = Boolean(isAuthenticated && userId);

      let zoomPercent;
      let preferredMode;

      if (eventDetail?.zoomPercent != null) {
        zoomPercent = clampZoomPercent(eventDetail.zoomPercent, LOGIN_APP_ZOOM_PERCENT);
        preferredMode = eventDetail?.zoomMode ?? APP_ZOOM_MODE_VISUAL;
      } else if (isLoggedIn) {
        const stored = getStoredZoomForUser(userId);
        zoomPercent = stored.zoomPercent;
        preferredMode = stored.zoomMode;
      } else {
        zoomPercent = LOGIN_APP_ZOOM_PERCENT;
        preferredMode = APP_ZOOM_MODE_VISUAL;
      }
      const effectiveMode =
        preferredMode === APP_ZOOM_MODE_VISUAL && isChromiumBrowser()
          ? APP_ZOOM_MODE_VISUAL
          : APP_ZOOM_MODE_COMPACT;

      const applyVisualZoomLayout = () => {
        const scaleFactor = zoomPercent / 100;
        const compensatedWidthPercent = 100 / scaleFactor;
        rootElement.style.width = `${compensatedWidthPercent}vw`;
        rootElement.style.maxWidth = 'none';
        rootElement.style.zoom = `${zoomPercent}%`;
        document.body.style.overflowX = 'hidden';
        htmlElement.style.overflowX = 'hidden';
        htmlElement.setAttribute('data-app-density', 'normal');
        htmlElement.style.setProperty('--sw-density-scale', '1');

        if (zoomPercent < 100) {
          htmlElement.setAttribute('data-app-zoom-below-100', 'true');
          htmlElement.style.setProperty('--sw-app-zoom-scale', String(scaleFactor));
        } else {
          htmlElement.removeAttribute('data-app-zoom-below-100');
          htmlElement.style.removeProperty('--sw-app-zoom-scale');
        }
      };

      if (effectiveMode === APP_ZOOM_MODE_VISUAL) {
        applyVisualZoomLayout();
      } else {
        rootElement.style.zoom = '';
        rootElement.style.width = '';
        rootElement.style.maxWidth = '';
        document.body.style.overflowX = '';
        htmlElement.style.overflowX = '';
        htmlElement.removeAttribute('data-app-zoom-below-100');
        htmlElement.style.removeProperty('--sw-app-zoom-scale');
        htmlElement.setAttribute('data-app-density', 'compact');
        const densityScale = Math.max(0.84, Math.min(1, zoomPercent / 100));
        htmlElement.style.setProperty('--sw-density-scale', String(densityScale));
      }
      */

      // Notify width-aware components once after layout settles.
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        scheduleTabsIndicatorZoomFix({ burst: true });
        scheduleFullCalendarZoomFix();
        scheduleAutocompleteZoomFix({ burst: true });
      });
    };

    const handleZoomUpdate = () => {
      applyDisplayPreferences();
      // Original: applyDisplayPreferences(event?.detail);
    };

    applyDisplayPreferences();
    window.addEventListener('app_zoom_update', handleZoomUpdate);
    const unsubscribeTabsIndicatorZoomFix = subscribeTabsIndicatorZoomFix();
    const unsubscribeGlobalFullCalendarZoomFix = subscribeGlobalFullCalendarZoomFix();
    const unsubscribeGlobalAutocompleteZoomFix = subscribeGlobalAutocompleteZoomFix();

    return () => {
      unsubscribeTabsIndicatorZoomFix();
      unsubscribeGlobalFullCalendarZoomFix();
      unsubscribeGlobalAutocompleteZoomFix();
      window.removeEventListener('app_zoom_update', handleZoomUpdate);
      rootElement.style.zoom = previousRootZoom;
      rootElement.style.width = previousRootWidth;
      rootElement.style.maxWidth = previousRootMaxWidth;
      document.body.style.overflowX = previousBodyOverflowX;
      htmlElement.style.overflowX = previousHtmlOverflowX;
      if (previousDensity) {
        htmlElement.setAttribute('data-app-density', previousDensity);
      } else {
        htmlElement.removeAttribute('data-app-density');
      }
      if (previousDensityScale) {
        htmlElement.style.setProperty('--sw-density-scale', previousDensityScale);
      } else {
        htmlElement.style.removeProperty('--sw-density-scale');
      }
      htmlElement.removeAttribute('data-app-zoom-below-100');
      htmlElement.style.removeProperty('--sw-app-zoom-scale');
    };
  }, [isAuthenticated]);

  return (
    <ThemeConfig>
      <ThemePrimaryColor>
        <RtlLayout>
          <NotistackProvider>
            <RouteChangeCancellation />
            <Settings />
            {/* {isAuthenticated ? <FloatingChatButton /> : null } */}
            <ScrollToTop />
            <NewRelicRouteTracker />
            <GoogleAnalytics />
            {isInitialized ? <Router /> : <LoadingScreen />}
          </NotistackProvider>
        </RtlLayout>
      </ThemePrimaryColor>
    </ThemeConfig>
  );
}
